import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, Trash2, CheckCheck, Sparkles, Info, AlertTriangle, IdCard, Calendar } from "lucide-react";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const KIND_ICON = {
    info: Info,
    success: Check,
    warning: AlertTriangle,
    interview: Calendar,
    passport: IdCard,
    system: Sparkles,
};

const KIND_STYLE = {
    info: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    interview: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    passport: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    system: "bg-muted text-muted-foreground",
};

const timeAgo = (iso) => {
    if (!iso) return "";
    const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
};

export const NotificationBell = () => {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications", { params: { limit: 10 } });
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch { /* silent — auth pending or offline */ }
    }, [user]);

    useEffect(() => {
        load();
        if (!user) return;
        const t = setInterval(load, 30_000);
        return () => clearInterval(t);
    }, [user, load]);

    const markAllRead = async () => {
        try {
            await api.post("/notifications/read", {});
            setUnread(0);
            setItems((xs) => xs.map((n) => ({ ...n, read: true })));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const removeNotif = async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            setItems((xs) => xs.filter((n) => n.id !== id));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    if (!user) return null;

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
                <button
                    data-testid="notification-bell"
                    aria-label="Notifications"
                    className="relative inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-foreground hover:bg-muted transition-colors"
                >
                    <Bell size={16} />
                    {unread > 0 && (
                        <span
                            data-testid="notification-unread-badge"
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-green text-white text-[10px] font-bold flex items-center justify-center border-2 border-background"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="end"
                data-testid="notification-dropdown"
                className="w-96 p-0 max-h-[520px] overflow-hidden"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h4 className="font-heading font-semibold text-sm">Notifications</h4>
                    {unread > 0 && (
                        <button
                            onClick={markAllRead}
                            data-testid="notif-mark-all-read"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"
                        >
                            <CheckCheck size={13} /> Mark all read
                        </button>
                    )}
                </div>
                <div className="max-h-[380px] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            <Bell size={22} className="mx-auto mb-2 opacity-50" />
                            You&apos;re all caught up.
                        </div>
                    ) : items.map((n) => {
                        const Icon = KIND_ICON[n.kind] || Info;
                        return (
                            <Link
                                key={n.id}
                                to={n.href || "/notifications"}
                                onClick={() => setOpen(false)}
                                data-testid={`notif-item-${n.id}`}
                                className={`flex gap-3 px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors ${!n.read ? "bg-emerald-500/5" : ""}`}
                            >
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${KIND_STYLE[n.kind] || KIND_STYLE.info}`}>
                                    <Icon size={14} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-foreground truncate">{n.title}</div>
                                    {n.body && <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{n.body}</div>}
                                    <div className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</div>
                                </div>
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeNotif(n.id); }}
                                    className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-muted-foreground hover:text-destructive"
                                    aria-label="Dismiss"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </Link>
                        );
                    })}
                </div>
                <Link
                    to="/notifications"
                    onClick={() => setOpen(false)}
                    data-testid="notif-see-all"
                    className="block text-center py-3 text-xs font-semibold text-brand-green hover:bg-muted border-t border-border"
                >
                    See all notifications →
                </Link>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default NotificationBell;
